import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { CalendarTimeAndType } from "../CalendarTimeAndType.tsx"

import {
  estimateEventHeightPx,
  pickEventCardTier,
  timeRangeStart
} from "../../helpers/calendar-event-card-layout.ts"
import {
  calendarEventCardAddress,
  calendarEventCardCustomerName,
  calendarEventCardRecordParts,
  calendarEventCardText
} from "../../helpers/calendar-event-card-text.ts"
import { useElementSize } from "../../hooks/use-element-size.ts"

const { Text } = Typography

interface CalendarEventCellEventProps {
  eventInfo: EventContentArg
}

const TypeDot = ({ color }: { color?: string }) => (
  <span
    className="inline-block shrink-0 size-1.5 rounded-full"
    style={{ backgroundColor: color }}
  />
)

export function CalendarEventCellEvent({
  eventInfo
}: CalendarEventCellEventProps) {
  const { t } = useTranslation()
  const event = eventInfo?.event
  const input = { title: event?.title, record: event?.extendedProps }
  const { title: storedTitle, typeLabel } = calendarEventCardText(input, t)
  // The grid card's one scannable headline is the appointment TYPE, not a
  // free-typed title — production/test titles ("1 hour", "15 mins event",
  // "Appointment: Walsh Home Renovation") otherwise buried the actual
  // category ("Site Visit", "Lead Meeting") as a truncated fragment next to
  // the time. `calendarEventCardText` still prefers the stored title (this
  // component just doesn't use it that way) — the detail panel and other
  // calendar surfaces that DO show the real title are untouched; this is
  // scoped to the grid summary card alone, which has no room to show both
  // without one crowding the other. A legacy event with no category at all
  // still falls back to its stored title.
  const title = typeLabel || storedTitle
  const { recordName, personName } = calendarEventCardRecordParts(input, t)
  // Once the customer's name heads the title (`Lead Meeting: John Smith`),
  // repeating it on a context line would just echo the headline back — that
  // line falls through to the address instead.
  const isCustomerInTitle = !!calendarEventCardCustomerName(input)
  const personOrAddress =
    (isCustomerInTitle ? null : personName) || calendarEventCardAddress(input)
  // The record (lead/project) takes the first context line when there is
  // one; an event with no lead/project (e.g. a personal event with just a
  // contact) puts the person/address there instead rather than waiting for
  // the taller second-line threshold that only makes sense once a first
  // line already exists to add a second one after.
  const firstContextText = recordName || personOrAddress
  const secondContextText = recordName ? personOrAddress : null

  // The card surfaces the event's relevant fields — type, time, who/what
  // it's for — compressing the LAYOUT as the box gets smaller rather than
  // deleting the data: a taller box stacks type/time/record/person on
  // their own lines; a shorter one puts type and time on one compact row
  // and drops the context lines, since there's no room to add them without
  // reading as a details view (reported live, twice, from opposite
  // directions — first two context lines crowding a merely-tall box, then
  // an over-correction that hid time and context even where they'd have
  // fit).
  //
  // What decides all of this is the box's real rendered size, not its
  // duration: FullCalendar's `eventMinHeight` (Calendar.tsx, a true safety
  // floor — see its own comment) and calendar.less' `@slot-height` are what
  // make a real 15-minute event genuinely shorter than a real 30-minute
  // one, so a height/width cutoff can decide this safely where a duration
  // cutoff (`diffInMinutes >= 30`) once put lines on a box that had no
  // pixels for them (confirmed live; invisible to jsdom).
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")
  const { ref: sizeRef, size } = useElementSize<HTMLDivElement>({
    width: 200,
    height: estimateEventHeightPx(diffInMinutes)
  })
  // Duration/box-size drives which lines show — never how big any of them
  // are. Typography, gap and padding are fixed design-system sizes for
  // every event regardless of duration: a 60-minute card is not a 30-minute
  // card "scaled up", it's the same card with more empty space below it
  // (reported live — a duration-scaled title/time/gap made a 60+ minute
  // card read as an oversized data block next to its neighbors).
  const { timePlacement, timeContent, showContext, showSecondContext } =
    pickEventCardTier(size)
  const TITLE_SIZE_CLASS = "text-[13px]"
  const TIME_SIZE_CLASS = "text-[11px]"
  const DETAIL_SIZE_CLASS = "text-[11px]"
  // `leading-[1.2]` (not `leading-none`) gives every line just enough
  // headroom for descenders that a real browser's glyph metrics need past
  // exactly 1x font-size — confirmed live; jsdom can't see a clipped
  // descender. The small fixed gap keeps adjacent lines from visually
  // touching without needing to grow with the box.
  const LINE_GAP = 2
  // Indents the stacked time/context lines under the title text rather than
  // the dot — matches the reviewed mock, and keeps every secondary line's
  // left edge aligned with the one above it.
  const secondaryLineIndent = "pl-[10px]"
  // "hidden" and "inline" both share the title's own row (a stacked line is
  // the only placement that doesn't) — "hidden" just means the actual time
  // *text* is dropped for width, while the (much shorter) type label can
  // still ride that row on its own.
  const sharesRowWithTitle = timePlacement !== "stacked"
  const timeString =
    timePlacement === "hidden" || !eventInfo?.timeText
      ? null
      : timeContent === "full"
        ? eventInfo.timeText
        : timeRangeStart(eventInfo.timeText)

  // Struck through rather than hidden: whoever was invited needs to see that
  // the meeting was cancelled, not find it silently gone from the grid.
  const isCancelled = event?.extendedProps?.status === "CANCELLED"

  // Month cells are a fraction of a week/day slot's height, so they get one
  // line.
  const isMonthView = eventInfo?.view?.type === "dayGridMonth"
  // FullCalendar's list view already renders the time and a colored dot in
  // its own cells (`.fc-list-event-time` / `-graphic`) — eventContent here
  // only fills the title cell, so this is title + a compact Lead/Project ·
  // participants · status line, not a rebuild of the whole row.
  const isListView = !!eventInfo?.view?.type?.startsWith("list")

  if (isListView) {
    const participantNames = event?.extendedProps?.participants
      ?.map((p: { full_name?: string }) => p?.full_name)
      .filter(Boolean)

    return (
      <Flex
        vertical
        gap={2}
        className={isCancelled ? "line-through opacity-60" : ""}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <Flex align="center" gap={6}>
          <Text strong className="text-sm">
            {title}
          </Text>
          {isCancelled && (
            <Text type="danger" className="text-[11px] font-medium">
              Cancelled
            </Text>
          )}
        </Flex>
        {!!(recordName || personOrAddress || participantNames?.length) && (
          <Text type="secondary" className="text-xs">
            {[recordName, personOrAddress, participantNames?.join(", ")]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        )}
      </Flex>
    )
  }

  if (isMonthView) {
    return (
      <Flex
        align="center"
        gap={4}
        className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <TypeDot color={event?.borderColor} />
        {eventInfo?.timeText && (
          <Text className="text-xs text-inherit opacity-80 leading-4 shrink-0">
            {eventInfo.timeText}
          </Text>
        )}
        <Text ellipsis className="text-xs text-inherit font-medium leading-4">
          {title}
        </Text>
      </Flex>
    )
  }

  // Title first, time second: the title is what a reader scans for, and the
  // event's own duration/color already anchor "when" spatially on the grid.
  // Time previously rendered ABOVE the title, which put the taller, bolder
  // title line in the space the shorter box had already spent on the time
  // line above it — confirmed live as the exact cause of the two lines
  // visually colliding on a 30-minute slot.
  //
  // `leading-none` (line-height: 1) was tried first to keep each line's box
  // as close to its font size as possible, but a single-line `Text` clips
  // at exactly that box height, and a real browser's glyph metrics need
  // slightly more than 1x font-size to show a descender in full — "Meeting"
  // rendered with its "g" cut off (confirmed live; invisible in jsdom).
  // `leading-[1.2]` gives every line just enough headroom for descenders
  // without the line boxes overlapping — `pickEventCardTier`'s thresholds
  // are what keeps the box itself tall enough for however many of these
  // lines it decides to show.
  //
  // `CalendarTimeAndType`'s time text is deliberately `shrink-0` (it either
  // shows in full or not at all — see that component), so a column too
  // narrow to fit it would otherwise force the whole row wider than the
  // card and let the excess spill past the outer `overflow-hidden` instead
  // of the flexbox truncating it — confirmed live on two adjacent
  // overlapping events. `timePlacement`/`timeContent` (both width-gated)
  // keep whatever's shown at a size that content actually fits, rather than
  // letting it overflow: sharing the type's row, its own stacked line once
  // the box supports one, or dropped outright below a low width floor.
  //
  // `overflow-wrap: anywhere` is a second line of defense alongside
  // `ellipsis` for a long *unbroken* value (a name with no spaces) — plain
  // `ellipsis` truncates any single-line text correctly on its own, but a
  // value long enough to be one giant "word" has, in the past, been the
  // kind of input that finds the edge case in a shared `Text` component.
  // The type already rides the title itself now, so this row/line only ever
  // carries time — it renders just when there's actually a time to show,
  // never an empty wrapper next to the title.
  const showInlineSlot = sharesRowWithTitle && !!timeString
  const showStackedSlot = !sharesRowWithTitle && !!timeString

  return (
    <Flex
      ref={sizeRef}
      vertical
      gap={LINE_GAP}
      className={`overflow-hidden size-full min-h-0 ${isCancelled ? "line-through opacity-60" : ""}`}
      data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
    >
      <Flex align="center" gap={4} className="min-w-0 shrink-0">
        <TypeDot color={event?.borderColor} />
        {showInlineSlot ? (
          // No gap here — the type and time must sit right next to each
          // other, separated only by the "· " already baked into the time
          // text. The wrapper (not the type) is what stretches to fill the
          // row, so a short type doesn't drag the time to the far edge.
          <Flex align="baseline" className="min-w-0 flex-1">
            <Text
              ellipsis
              className={`${TITLE_SIZE_CLASS} text-inherit font-semibold leading-[1.2] min-w-0 shrink [overflow-wrap:anywhere]`}
            >
              {title}
            </Text>
            <CalendarTimeAndType
              className={`${TIME_SIZE_CLASS} opacity-75 leading-[1.2]`}
              time={`· ${timeString}`}
            />
          </Flex>
        ) : (
          <Text
            ellipsis
            className={`${TITLE_SIZE_CLASS} text-inherit font-semibold leading-[1.2] min-w-0 flex-1 [overflow-wrap:anywhere]`}
          >
            {title}
          </Text>
        )}
      </Flex>

      {showStackedSlot && (
        <CalendarTimeAndType
          className={`${TIME_SIZE_CLASS} opacity-75 leading-[1.2] shrink-0 ${secondaryLineIndent}`}
          time={timeString}
        />
      )}

      {firstContextText && showContext && (
        <Text
          ellipsis
          className={`${DETAIL_SIZE_CLASS} text-inherit opacity-90 leading-[1.2] shrink-0 [overflow-wrap:anywhere] ${secondaryLineIndent}`}
        >
          {firstContextText}
        </Text>
      )}

      {secondContextText && showSecondContext && (
        <Text
          ellipsis
          className={`${DETAIL_SIZE_CLASS} text-inherit opacity-70 leading-[1.2] shrink-0 [overflow-wrap:anywhere] ${secondaryLineIndent}`}
        >
          {secondContextText}
        </Text>
      )}
    </Flex>
  )
}
